import { useEffect, useState } from "react";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle, Clock, Award, Users, Briefcase, GraduationCap, ArrowRight } from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { format } from "date-fns";

const CounselingPage = () => {
  const [counselors, setCounselors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCounselor, setSelectedCounselor] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [booking, setBooking] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    topic: "",
    message: "",
  });

  const topics = [
    "Career Change",
    "Resume Review",
    "Interview Preparation",
    "Salary Negotiation",
    "Career Planning",
    "Leadership Development",
    "Work-Life Balance",
    "Other",
  ];

  useEffect(() => {
    const fetchCounselors = async () => {
      try {
        const response = await axios.get(`${API}/counselors`);
        setCounselors(response.data);
      } catch (error) {
        console.error("Error fetching counselors:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchCounselors();
  }, []);

  const handleBookSession = (counselor) => {
    setSelectedCounselor(counselor);
    setShowBookingModal(true);
  };

  const handleSubmitBooking = async (e) => {
    e.preventDefault();
    
    if (!selectedDate || !selectedSlot) {
      toast.error("Please select a date and time slot");
      return;
    }

    setBooking(true);
    try {
      await axios.post(`${API}/counseling/book`, {
        ...formData,
        date: format(selectedDate, "yyyy-MM-dd"),
        time_slot: selectedSlot,
        counselor_id: selectedCounselor.id,
      });
      toast.success("Booking confirmed! Check your email for details.");
      setShowBookingModal(false);
      setFormData({ name: "", email: "", phone: "", topic: "", message: "" });
      setSelectedDate(null);
      setSelectedSlot("");
    } catch (error) {
      toast.error("Failed to book session. Please try again.");
    } finally {
      setBooking(false);
    }
  };

  const benefits = [
    {
      icon: Briefcase,
      title: "Career Strategy",
      description: "Get a personalized roadmap for your career growth",
    },
    {
      icon: GraduationCap,
      title: "Skill Assessment",
      description: "Identify gaps and learn what skills to develop",
    },
    {
      icon: Award,
      title: "Industry Insights",
      description: "Gain insider knowledge from experienced professionals",
    },
    {
      icon: Users,
      title: "Network Building",
      description: "Learn strategies to expand your professional network",
    },
  ];

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-slate-900 text-white py-20" data-testid="counseling-hero">
        <div className="container-padding">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <span className="overline text-lime-500">Career Counseling</span>
              <h1 className="font-heading text-4xl lg:text-5xl font-light">
                Get Expert Guidance for Your Career Journey
              </h1>
              <p className="text-slate-300 text-lg">
                Book a one-on-one session with our certified career counselors. Get personalized 
                advice, resume reviews, interview coaching, and strategic career planning.
              </p>
              <div className="flex flex-wrap gap-6 text-slate-300">
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-lime-500" />
                  Certified Counselors
                </span>
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-lime-500" />
                  1-on-1 Sessions
                </span>
                <span className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-lime-500" />
                  Flexible Scheduling
                </span>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/4427552/pexels-photo-4427552.jpeg?auto=compress&cs=tinysrgb&w=600"
                alt="Career counseling"
                className="rounded-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-padding bg-white" data-testid="benefits-section">
        <div className="container-padding">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="overline">Why Career Counseling</span>
            <h2 className="heading-2 mt-4">Transform Your Professional Life</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="text-center p-8 border border-slate-100 hover:shadow-xl transition-all duration-300"
                data-testid={`benefit-card-${index}`}
              >
                <div className="w-16 h-16 bg-lime-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <benefit.icon className="w-8 h-8 text-lime-600" />
                </div>
                <h3 className="font-heading text-xl font-medium text-slate-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-slate-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Counselors */}
      <section className="section-padding bg-slate-50" data-testid="counselors-section">
        <div className="container-padding">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="overline">Our Counselors</span>
            <h2 className="heading-2 mt-4">Meet Our Expert Counselors</h2>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-2xl p-8 animate-pulse">
                  <div className="w-24 h-24 rounded-full bg-slate-200 mx-auto mb-6" />
                  <div className="h-6 bg-slate-200 rounded w-3/4 mx-auto mb-4" />
                  <div className="h-4 bg-slate-200 rounded w-1/2 mx-auto" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {counselors.map((counselor) => (
                <div
                  key={counselor.id}
                  className="bg-white rounded-2xl p-8 text-center hover:shadow-xl transition-all duration-300"
                  data-testid={`counselor-card-${counselor.id}`}
                >
                  <img
                    src={counselor.image_url}
                    alt={counselor.name}
                    className="w-24 h-24 rounded-full object-cover mx-auto mb-6"
                  />
                  <h3 className="font-heading text-xl font-medium text-slate-900 mb-1">
                    {counselor.name}
                  </h3>
                  <p className="text-lime-600 font-medium mb-2">{counselor.title}</p>
                  <p className="text-slate-500 text-sm mb-4">{counselor.specialization}</p>
                  <p className="text-slate-600 text-sm mb-6 line-clamp-3">{counselor.bio}</p>
                  <div className="flex items-center justify-center gap-4 text-sm text-slate-500 mb-6">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {counselor.experience}
                    </span>
                  </div>
                  <Button
                    onClick={() => handleBookSession(counselor)}
                    className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-full"
                    data-testid={`book-counselor-btn-${counselor.id}`}
                  >
                    Book Session
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Booking Modal */}
      <Dialog open={showBookingModal} onOpenChange={setShowBookingModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              Book a Session with {selectedCounselor?.name}
            </DialogTitle>
            <DialogDescription>
              Fill in your details to schedule a career counseling session.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmitBooking} className="space-y-6 mt-4" data-testid="booking-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Full Name *
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="John Doe"
                  data-testid="booking-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Email *
                </label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  placeholder="john@example.com"
                  data-testid="booking-email-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Phone Number *
                </label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  placeholder="+1 234 567 890"
                  data-testid="booking-phone-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Topic *
                </label>
                <Select
                  value={formData.topic}
                  onValueChange={(value) => setFormData({ ...formData, topic: value })}
                  required
                >
                  <SelectTrigger data-testid="booking-topic-select">
                    <SelectValue placeholder="Select topic" />
                  </SelectTrigger>
                  <SelectContent>
                    {topics.map((topic) => (
                      <SelectItem key={topic} value={topic}>
                        {topic}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Select Date *
              </label>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                disabled={(date) => date < new Date() || date.getDay() === 0 || date.getDay() === 6}
                className="rounded-lg border"
                data-testid="booking-calendar"
              />
            </div>

            {selectedDate && selectedCounselor && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Select Time Slot *
                </label>
                <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                  {selectedCounselor.available_slots?.map((slot) => (
                    <Button
                      key={slot}
                      type="button"
                      variant={selectedSlot === slot ? "default" : "outline"}
                      className={`${selectedSlot === slot ? "bg-lime-500 text-slate-900" : ""}`}
                      onClick={() => setSelectedSlot(slot)}
                      data-testid={`time-slot-${slot.replace(/\s+/g, '-')}`}
                    >
                      {slot}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Additional Message (Optional)
              </label>
              <Textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                placeholder="Tell us more about what you'd like to discuss..."
                rows={3}
                data-testid="booking-message-input"
              />
            </div>

            <Button
              type="submit"
              disabled={booking}
              className="w-full bg-lime-500 text-slate-900 hover:bg-lime-400 rounded-full py-6"
              data-testid="submit-booking-btn"
            >
              {booking ? "Booking..." : "Confirm Booking"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default CounselingPage;
